import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { Lock, AlertCircle, Mail, Eye, EyeOff, UserPlus, Shield } from 'lucide-react';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSetupMode, setIsSetupMode] = useState(false);

  const { login, setupFirstAdmin, isAuthenticated, isFirstSetup } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin');
    }
    // Si premier setup, activer le mode configuration
    if (isFirstSetup) {
      setIsSetupMode(true);
    }
  }, [isAuthenticated, isFirstSetup, navigate]);

  const validateForm = () => {
    if (!email) {
      setError('Veuillez entrer votre email');
      return false;
    }
    if (!password) {
      setError('Veuillez entrer votre mot de passe');
      return false;
    }
    if (isSetupMode && password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    let result;
    if (isSetupMode) {
      result = await setupFirstAdmin(email, password);
    } else {
      result = await login(email, password);
    }

    if (result.success) {
      navigate('/admin');
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-600 to-red-800 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            {isSetupMode ? (
              <UserPlus className="w-10 h-10 text-red-600" />
            ) : (
              <Lock className="w-10 h-10 text-red-600" />
            )}
          </div>
          <h1 className="text-2xl font-black text-gray-800 mb-1">Le Family's</h1>
          <p className="text-gray-500">
            {isSetupMode ? 'Configuration initiale' : 'Accès Back Office'}
          </p>
        </div>

        {isSetupMode && (
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-6">
            <div className="flex items-center gap-2 mb-1">
              <Shield className="w-5 h-5" />
              <p className="font-semibold">Premier accès détecté</p>
            </div>
            <p className="text-sm">Créez votre compte Super Admin pour commencer.</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(''); }}
                onKeyDown={handleKeyDown}
                placeholder="admin@lefamilys.fr"
                disabled={loading}
                autoComplete="email"
                autoFocus
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:border-red-500 focus:ring-4 focus:ring-red-100 outline-none transition-all disabled:opacity-50 disabled:bg-gray-50"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mot de passe
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); setError(''); }}
                onKeyDown={handleKeyDown}
                placeholder="••••••••"
                disabled={loading}
                autoComplete={isSetupMode ? 'new-password' : 'current-password'}
                className="w-full pl-10 pr-12 py-3 border border-gray-200 rounded-xl focus:border-red-500 focus:ring-4 focus:ring-red-100 outline-none transition-all disabled:opacity-50 disabled:bg-gray-50"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {isSetupMode && (
              <p className="text-xs text-gray-500 mt-1">Minimum 6 caractères</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full py-3 mt-6"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                {isSetupMode ? 'Configuration...' : 'Connexion...'}
              </span>
            ) : (
              isSetupMode ? 'Créer mon compte Super Admin' : 'Se connecter'
            )}
          </Button>
        </form>

        <div className="mt-8 pt-6 border-t border-gray-100">
          <p className="text-center text-xs text-gray-400">
            Connexion sécurisée via Firebase Authentication
          </p>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-xs text-gray-500">Chiffrement SSL/TLS</span>
          </div>
        </div>
      </Card>
    </div>
  );
};
